import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, ChevronRight, X, Save, Loader, AlertTriangle, Calendar } from 'lucide-react';
import ApiService from './ApiService';
import { usePermissions, PERMISSIONS } from './usePermissions';
import AccessDenied from './AccessDenied';

// Modal Component - For Add/Edit Service
const ServiceModal = ({ isOpen, onClose, onSubmit, title, submitText, formData, setFormData, error, saving, t }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.service.singular} Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                placeholder="e.g., HRT - Women, Lab Work - Comprehensive"
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent bg-white"
              >
                <option value="">Select a category...</option>
                <option value="Hormone Therapy">Hormone Therapy</option>
                <option value="Diagnostic">Diagnostic/Lab Work</option>
                <option value="Appointment">Consultation/Appointment</option>
                <option value="Treatment">Treatment/Procedure</option>
                <option value="Monitoring">Monitoring/Check-up</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Interval Months */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Frequency (Months) <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.intervalMonths}
                onChange={(e) => setFormData({ ...formData, intervalMonths: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent bg-white"
                required
              >
                <option value="">How often?</option>
                <option value="1">Every 1 month</option>
                <option value="2">Every 2 months</option>
                <option value="3">Every 3 months</option>
                <option value="4">Every 4 months</option>
                <option value="6">Every 6 months</option>
                <option value="12">Every 12 months (annually)</option>
              </select>
            </div>

            {/* Reminder Days Before */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Send Reminder (Days Before) <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.reminderDaysBefore}
                onChange={(e) => setFormData({ ...formData, reminderDaysBefore: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent bg-white"
                required
              >
                <option value="">When to remind?</option>
                <option value="3">3 days before</option>
                <option value="7">7 days before (1 week)</option>
                <option value="14">14 days before (2 weeks)</option>
                <option value="30">30 days before (1 month)</option>
                <option value="60">60 days before (2 months)</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                placeholder="Brief description of the service"
                rows="3"
              />
            </div>

            {/* Preparation & Information Links */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Preparation & Information Links (Optional)
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Add preparation guides, instructional videos, or resources to help patients prepare for this service
              </p>

              {/* Existing Links */}
              {formData.links && formData.links.length > 0 && (
                <div className="space-y-2 mb-3">
                  {formData.links.map((link, index) => (
                    <div key={link.id || index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{link.title}</p>
                        <p className="text-xs text-gray-500 truncate">{link.url}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newLinks = formData.links.filter((_, i) => i !== index);
                          setFormData({ ...formData, links: newLinks });
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Link Form */}
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Link title (e.g., Fasting instructions)"
                  value={formData.newLinkTitle || ''}
                  onChange={(e) => setFormData({ ...formData, newLinkTitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="URL (https://youtube.com/...)"
                    value={formData.newLinkUrl || ''}
                    onChange={(e) => setFormData({ ...formData, newLinkUrl: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.newLinkTitle || !formData.newLinkUrl) {
                        alert('Please enter both title and URL');
                        return;
                      }
                      try {
                        new URL(formData.newLinkUrl);
                      } catch (e) {
                        alert('Please enter a valid URL');
                        return;
                      }
                      const newLink = {
                        id: Date.now().toString(),
                        title: formData.newLinkTitle.trim(),
                        url: formData.newLinkUrl.trim(),
                        addedAt: new Date().toISOString()
                      };
                      const links = formData.links || [];
                      setFormData({
                        ...formData,
                        links: [...links, newLink],
                        newLinkTitle: '',
                        newLinkUrl: ''
                      });
                    }}
                    className="px-4 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 transition text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {submitText}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ServicesPage = ({ config, onSelectService, currentUser }) => {
  const t = config?.terminology || {
    service: { singular: 'Service', plural: 'Services', singularLower: 'service', pluralLower: 'services' }
  };

  const { hasPermission } = usePermissions(currentUser);

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [patientAssignments, setPatientAssignments] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [errorModal, setErrorModal] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    intervalMonths: '',
    reminderDaysBefore: '30',
    description: '',
    links: [],
    newLinkTitle: '',
    newLinkUrl: ''
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getServices();
      setServices(data);
    } catch (err) {
      setError('Failed to load services');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const newService = await ApiService.createService(formData);
      setServices([...services, newService]);
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to create service');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditService = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await ApiService.updateService(selectedService.serviceId, formData);
      setServices(services.map(s => 
        s.serviceId === selectedService.serviceId 
          ? { ...s, ...formData } 
          : s
      ));
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to update service');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (service) => {
    setServiceToDelete(service);
    setPatientAssignments(null); // Reset
    
    // Check if service is assigned to patients
    try {
      const response = await ApiService.checkServiceAssignments(service.serviceId);
      setPatientAssignments(response);
    } catch (err) {
      console.error('Error checking assignments:', err);
      setPatientAssignments(null);
    }
    
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!serviceToDelete) return;
    
    setDeleting(true);
    try {
      await ApiService.deleteService(serviceToDelete.serviceId);
      setServices(services.filter(s => s.serviceId !== serviceToDelete.serviceId));
      setShowDeleteConfirm(false);
      setServiceToDelete(null);
    } catch (err) {
      console.error('Failed to delete service:', err);
      
      // Parse error response for patient assignment details
      let errorTitle = 'Error Deleting Service';
      let errorMessage = 'Failed to delete service. Please try again.';
      
      try {
        const jsonMatch = err.message.match(/\{.*\}/);
        if (jsonMatch) {
          const errorData = JSON.parse(jsonMatch[0]);
          
          if (errorData.message && errorData.message.includes('assigned to patients')) {
            errorTitle = 'Service In Use';
            const patientCount = errorData.patientCount || 0;
            const samplePatients = errorData.samplePatients || [];
            const totalAssignments = errorData.totalAssignments || 0;
            
            errorMessage = `Cannot delete "${serviceToDelete.name}" because it is currently assigned to ${patientCount} patient${patientCount !== 1 ? 's' : ''} (${totalAssignments} total assignment${totalAssignments !== 1 ? 's' : ''}).`;
            
            if (samplePatients.length > 0) {
              errorMessage += `\n\nPatients currently using this service:\n• ${samplePatients.join('\n• ')}`;
              if (patientCount > samplePatients.length) {
                errorMessage += `\n• ...and ${patientCount - samplePatients.length} more`;
              }
            }
            
            errorMessage += '\n\nPlease remove this service from all patients before deleting it from the catalog.';
          } else {
            errorMessage = errorData.message || errorMessage;
          }
        } else {
          errorMessage = err.message || errorMessage;
        }
      } catch (parseErr) {
        console.error('Error parsing error message:', parseErr);
        errorMessage = err.message || errorMessage;
      }
      
      setErrorModal({ title: errorTitle, message: errorMessage });
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (service) => {
    setSelectedService(service);
    setFormData({
      name: service.name,
      category: service.category || '',
      intervalMonths: service.intervalMonths?.toString() || '',
      reminderDaysBefore: service.reminderDaysBefore?.toString() || '30',
      description: service.description || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      intervalMonths: '',
      reminderDaysBefore: '30',
      description: '',
      links: [],
      newLinkTitle: '',
      newLinkUrl: ''
    });
    setSelectedService(null);
    setError('');
  };

  const filteredServices = services
    .filter(service =>
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (service.category && service.category.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Get category badge color
  const getCategoryColor = (category) => {
    const colors = {
      'Hormone Therapy': 'bg-pink-100 text-pink-800',
      'Diagnostic': 'bg-spruce-100 text-spruce-800',
      'Appointment': 'bg-green-100 text-green-800',
      'Treatment': 'bg-spruce-100 text-spruce-800',
      'Monitoring': 'bg-yellow-100 text-yellow-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  // Check if user has permission to view services
  if (!hasPermission(PERMISSIONS.SERVICES_VIEW)) {
    return <AccessDenied resource="services" action="view" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t.service.plural}</h2>
          <p className="text-gray-500">Manage available {t.service.pluralLower}</p>
        </div>
        {hasPermission(PERMISSIONS.SERVICES_ADD) && (
          <button 
            onClick={openAddModal}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 flex items-center gap-2"
          >
            <Plus size={20} />
            Add {t.service.singular}
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={`Search ${t.service.pluralLower}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : filteredServices.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No {t.service.pluralLower} found
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredServices.map(service => (
              <div key={service.serviceId} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-100 rounded">
                      <Calendar className="text-orange-600" size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">{service.name}</h3>
                      {service.category && (
                        <span className={`inline-block mt-2 px-2 py-1 rounded text-xs ${getCategoryColor(service.category)}`}>
                          {service.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-1 text-sm text-gray-600 mb-3">
                  <p><strong>Frequency:</strong> Every {service.intervalMonths} month{service.intervalMonths > 1 ? 's' : ''}</p>
                  <p><strong>Reminder:</strong> {service.reminderDaysBefore} days before</p>
                  {service.description && (
                    <p className="text-xs text-gray-500 mt-2">{service.description}</p>
                  )}
                </div>

                {/* Audit Trail */}
                {(service.createdBy || service.updatedBy) && (
                  <div className="text-xs text-gray-500 mb-3 space-y-1 border-t pt-2">
                    {service.createdBy && (
                      <div>
                        Created by: <span className="font-medium">{service.createdBy}</span>
                        {service.createdAt && (
                          <span className="ml-1">
                            on {new Date(service.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                    )}
                    {service.updatedBy && service.updatedBy !== service.createdBy && (
                      <div>
                        Updated by: <span className="font-medium">{service.updatedBy}</span>
                        {service.updatedAt && (
                          <span className="ml-1">
                            on {new Date(service.updatedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  {hasPermission(PERMISSIONS.SERVICES_EDIT) && (
                    <button 
                      onClick={() => openEditModal(service)}
                      className="flex-1 p-2 text-spruce-600 hover:bg-spruce-50 rounded text-sm flex items-center justify-center gap-1"
                      title="Edit service"
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                  )}
                  {hasPermission(PERMISSIONS.SERVICES_DELETE) && (
                    <button 
                      onClick={() => handleDeleteService(service)}
                      className="flex-1 p-2 text-red-600 hover:bg-red-50 rounded text-sm flex items-center justify-center gap-1"
                      title="Delete service"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Service Modal */}
      <ServiceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddService}
        title={`Add ${t.service.singular}`}
        submitText={`Add ${t.service.singular}`}
        formData={formData}
        setFormData={setFormData}
        error={error}
        saving={saving}
        t={t}
      />

      {/* Edit Service Modal */}
      <ServiceModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditService}
        title={`Edit ${t.service.singular}`}
        submitText="Save Changes"
        formData={formData}
        setFormData={setFormData}
        error={error}
        saving={saving}
        t={t}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && serviceToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delete {t.service.singular}?</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{serviceToDelete.name}</strong>?
            </p>
            
            {/* Show patient assignments if any */}
            {patientAssignments && patientAssignments.patientCount > 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-semibold text-yellow-800 mb-1">
                      Assigned to {patientAssignments.patientCount} patient{patientAssignments.patientCount !== 1 ? 's' : ''}
                    </p>
                    {patientAssignments.samplePatients && patientAssignments.samplePatients.length > 0 && (
                      <div className="text-sm text-yellow-700">
                        <p className="mb-1">Patients:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {patientAssignments.samplePatients.map((patient, idx) => (
                            <li key={idx}>{patient}</li>
                          ))}
                          {patientAssignments.patientCount > patientAssignments.samplePatients.length && (
                            <li>...and {patientAssignments.patientCount - patientAssignments.samplePatients.length} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                    <p className="text-sm text-yellow-700 mt-2">
                      You must remove this service from all patients before deleting it.
                    </p>
                  </div>
                </div>
              </div>
            ) : patientAssignments === null ? (
              <div className="text-sm text-gray-500 mb-4">
                Checking patient assignments...
              </div>
            ) : (
              <p className="text-sm text-gray-600 mb-4">
                This service is not currently assigned to any patients.
              </p>
            )}
            
            <p className="text-red-600 font-semibold mb-6">This action cannot be undone.</p>
            
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={deleting || (patientAssignments && patientAssignments.patientCount > 0)}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Yes, Delete
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setServiceToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6 whitespace-pre-line">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;